import { Injectable, computed, signal } from "@angular/core";

/** Handle returned by {@link LongOperationService.begin}. Idempotent: calling end() twice is safe. */
export interface LongOperationHandle {
    end(): void;
}

/**
 * Hard ceiling on how long an active long operation may keep the session alive.
 * A leaked handle (component destroyed mid-batch, early-return error path) must not
 * hold the iFirm session open forever -- that would defeat the host's login timeout.
 */
export const MAX_LONG_OPERATION_MS = 60 * 60 * 1000;

/**
 * Ref-counted scope for "a long operation is running".
 *
 * Replaces the old `AuthService.longOperationEmitter` boolean, which had no ref-counting:
 * whichever concurrent operation finished first emitted `false` and killed keepalive for
 * the others, and `close()` emitted `false` even when nothing had started.
 *
 * This service owns ONLY the "is something running" question. Suppressing grid refreshes
 * during a bulk change is a separate concern -- see BulkOperationService.
 */
@Injectable({ providedIn: "root" })
export class LongOperationService {
    private readonly activeCount = signal(0);
    /** Start time (epoch ms) of each currently-active operation, keyed by its handle. */
    private readonly startedAt = new Map<symbol, number>();

    public readonly isActive = computed(() => this.activeCount() > 0);

    /**
     * Marks a long operation as started. Prefer {@link run} so that end() lands in a
     * `finally` block; call this directly only when the scope cannot be expressed as
     * a single async function.
     */
    public begin(): LongOperationHandle {
        const key = Symbol("longOperation");
        this.startedAt.set(key, Date.now());
        this.activeCount.update((n) => n + 1);
        let ended = false;
        return {
            end: () => {
                if (ended) return;
                ended = true;
                this.startedAt.delete(key);
                this.activeCount.update((n) => Math.max(0, n - 1));
            },
        };
    }

    /** Runs `fn` inside a long-operation scope, releasing it even if `fn` throws. */
    public async run<T>(fn: () => Promise<T>): Promise<T> {
        const handle = this.begin();
        try {
            return await fn();
        } finally {
            handle.end();
        }
    }

    /**
     * True when an operation is active AND has not blown past {@link MAX_LONG_OPERATION_MS}.
     * The keepalive uses this -- not `isActive` -- so a leaked handle eventually stops
     * overriding idle detection.
     *
     * A stale handle suppresses the override even if a newer operation is also running:
     * a leak means the real elapsed time is unknown, so the safe reading is "expired".
     */
    public shouldOverrideIdle(): boolean {
        if (this.startedAt.size === 0) {
            return false;
        }
        const oldest = Math.min(...this.startedAt.values());
        return Date.now() - oldest < MAX_LONG_OPERATION_MS;
    }
}
